<section class="px-0 pt-[50px] pb-0 mb-[50px]">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs" />
    <div class="flex items-center gap-4 mb-0">
      <span
        class="px-3 py-1 bg-[#f5eeff] text-[#741fa2] rounded-md text-[10px] font-black uppercase tracking-widest"
        >Featured Guides</span
      >
    </div>

    <div class="mb-[30px] rounded-2xl text-left">
      <!-- No colour utility: H1 colour comes from the global heading token (styles.scss). -->
      <h1>
        {{ pageH1() || 'Coupon & Savings Blog | Best Deals, Promo Codes and Tips' }}
      </h1>
      <p class="mt-[5px] text-[16px] leading-[1.6] font-medium text-slate-900">
        {{
          pageShortDescription() ||
            'Explore verified coupon codes, daily discount deals, and practical shopping tips to save money on fashion, electronics, groceries, travel, and more.'
        }}
      </p>
    </div>

    @if (primaryFeaturedPost(); as primaryPost) {
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6 h-auto md:h-[620px]">
        <!-- `min-h` is what makes this visible on a phone. Every child of this card is absolutely
             positioned — image, gradient, logo, copy — so the card has no content in flow to give
             it height. On desktop the row supplies one (`md:h-[620px]` on the grid above), but
             below that breakpoint the grid is `h-auto`, so the card collapsed to zero and the
             featured post simply was not there. -->
        <div
          class="md:col-span-8 group relative min-h-[420px] overflow-hidden rounded-[2.5rem] bg-slate-900 shadow-2xl md:min-h-0"
        >
          <img
            [src]="primaryPost.imageUrl"
            class="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-1000"
            [alt]="primaryPost.title"
          />
          <div class="absolute inset-0 sav-bento-gradient"></div>
          @if (primaryPost.storeLogoUrl) {
            <span
              class="absolute top-6 right-6 z-10 w-16 h-16 rounded-full bg-white shadow-lg overflow-hidden ring-1 ring-black/5"
            >
              <img
                [src]="primaryPost.storeLogoUrl"
                alt="Store logo"
                class="w-full h-full object-cover rounded-full"
              />
            </span>
          }
          <div class="absolute bottom-0 p-8 md:p-14">
            <div
              class="flex gap-4 mb-6 text-xs font-bold uppercase text-purple-200 tracking-widest"
            >
              <a
                [routerLink]="primaryPost.categoryRouteLink"
                class="hover:text-white/90 underline-offset-4 hover:underline"
                >{{ primaryPost.categoryName }}</a
              >
              <span>•</span>
              <a
                [routerLink]="primaryPost.authorRouteLink"
                class="hover:text-white/90 underline-offset-4 hover:underline"
                >{{ primaryPost.authorName }}</a
              >
              <span>•</span>
              <span>{{ primaryPost.readTimeLong }}</span>
            </div>
            <h2 class="type-on-dark mb-8 max-w-2xl">{{ primaryPost.title }}</h2>
            <a
              class="flex items-center gap-3 px-8 py-4 bg-white text-slate-900 rounded-full font-bold hover:bg-[var(--color-primary-tint)] hover:text-[var(--color-primary-deep)] transition-all transform hover:-translate-y-1 w-fit"
              [routerLink]="primaryPost.routeLink"
            >
              Start Reading
              <svg viewBox="0 0 24 24" fill="none" class="w-5 h-5" aria-hidden="true">
                <path
                  d="M5 12H19M12 5L19 12L12 19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </a>
          </div>
        </div>

        <div class="md:col-span-4 flex flex-col gap-6">
          @for (post of secondaryFeaturedPosts(); track post.id; let index = $index) {
            <!-- Same collapse as the primary card: `flex-1` gives these height only when the
                 column has one, which it does not on a phone. -->
            <a
              class="flex-1 group relative min-h-[220px] overflow-hidden rounded-[2rem] shadow-lg cursor-pointer md:min-h-0"
              [class.bg-[#4a148c]]="index === 0"
              [class.bg-emerald-900]="index === 1"
              [routerLink]="post.routeLink"
            >
              <img
                [src]="post.imageUrl"
                class="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-110 transition-transform duration-700"
                [alt]="post.title"
              />
              <div class="absolute inset-0 bg-black/20"></div>
              @if (post.storeLogoUrl) {
                <span
                  class="absolute top-5 right-5 z-10 w-12 h-12 rounded-full bg-white shadow-lg overflow-hidden ring-1 ring-black/5"
                >
                  <img
                    [src]="post.storeLogoUrl"
                    alt="Store logo"
                    class="w-full h-full object-cover rounded-full"
                  />
                </span>
              }
              <div class="relative h-full p-8 flex flex-col justify-end">
                <span
                  class="text-xs font-bold uppercase mb-3 tracking-widest"
                  [class.text-purple-200]="index === 0"
                  [class.text-emerald-300]="index === 1"
                  >{{ post.categoryName }}</span
                >
                <h3 class="type-on-dark">{{ post.title }}</h3>
                <span
                  class="mt-4 text-sm font-semibold underline-offset-4"
                  [class.text-purple-100]="index === 0"
                  [class.text-emerald-100]="index === 1"
                  >By {{ post.authorName }}</span
                >
              </div>
            </a>
          }
        </div>
      </div>
    }
  </div>
</section>

<section
  class="sav-popular-categories-section"
  aria-label="Popular categories"
  style="padding: 0; margin-bottom: 50px"
>
  <div class="sav-popular-categories-wrap">
    <!-- Heading on the left, "View all" on the right — the carousel only shows a slide at a time,
         so the full list needs a way in. -->
    <div class="sav-blog-categories-head">
      <div>
        <h2>Popular Blog Categories</h2>
        <p>Browse guides by the topic you are shopping for.</p>
      </div>
      <a class="sav-blog-categories-all" routerLink="/blogs/categories">
        View All Categories
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m9 5 7 7-7 7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </a>
    </div>

    <div class="sav-popular-categories-grid" id="popular-categories-panel">
      @for (category of visiblePopularCategories(); track category.label + $index) {
        <a [routerLink]="category.routeLink" class="sav-popular-category-card">
          <span
            class="sav-popular-category-thumb"
            aria-hidden="true"
            [style.background-image]="category.imageUrl ? 'url(' + category.imageUrl + ')' : null"
          ></span>
          <span class="sav-popular-category-label">{{ category.label }}</span>
        </a>
      }
    </div>

    <div class="sav-popular-categories-dots" aria-hidden="true">
      @for (index of popularCategorySlideIndexes(); track index) {
        <button
          type="button"
          [class.active]="currentPopularCategorySlide() === index"
          [attr.aria-label]="'Go to category slide ' + (index + 1)"
          (click)="selectPopularCategorySlide(index)"
        ></button>
      }
    </div>
  </div>
</section>

<section class="mb-[50px]">
  <div class="sav-site-container">
    <div #postsTop class="mb-16 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
      <div class="sav-section-head">
        <h2 class="sav-font-sora">
          {{ pageH2() || 'Smart Shopping Guides, Coupon Tips and Deal Updates' }}
        </h2>
        @if (descriptionHtml()) {
          <div
            class="sav-blog-guides-description mt-3 text-[16px] leading-[1.6] font-medium text-slate-700"
            [innerHTML]="descriptionHtml() | trustedCmsHtml"
          ></div>
        } @else {
          <p class="mt-3 text-[16px] leading-[1.6] font-medium text-slate-700">
            Read expert savings advice, verified coupon strategies, and weekly discount insights to
            cut costs on groceries, fashion, travel, and everyday essentials.
          </p>
        }
      </div>
    </div>

    <!-- Shared blog card + grid: 4 per row here, because this listing has no sidebar. -->
    <div class="sav-blog-grid mb-20">
      @for (post of paginatedPosts(); track post.id) {
        <app-blog-post-card
          [post]="{
            articlePath: post.routeLink,
            title: post.title,
            excerpt: post.excerpt,
            imageUrl: post.imageUrl,
            category: post.categoryName,
            readTime: post.readTime,
            author: post.authorName,
            authorPath: post.authorRouteLink,
            storeLogoUrl: post.storeLogoUrl,
          }"
        />
      }
    </div>

    @if (postsTotalPages() > 1) {
      <div class="flex justify-center items-center gap-4">
        <button
          class="w-14 h-14 rounded-2xl flex items-center justify-center text-slate-400 border border-slate-100 hover:text-[var(--color-primary)] transition shadow-sm bg-white disabled:cursor-not-allowed disabled:opacity-40"
          type="button"
          [disabled]="currentPostsPage() === 1"
          (click)="goToPreviousPostsPage()"
        >
          <svg viewBox="0 0 24 24" fill="none" class="w-6 h-6" aria-hidden="true">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </button>
        @for (item of postsPageItems(); track $index) {
          @if (item === 'ellipsis') {
            <span
              class="w-14 h-14 flex items-center justify-center font-bold text-slate-400"
              aria-hidden="true"
              >…</span
            >
          } @else {
            <button
              class="w-14 h-14 rounded-2xl font-bold border border-slate-100 bg-white text-slate-600 hover:border-[var(--color-primary)] transition-all"
              [class.shadow-xl]="currentPostsPage() === item"
              [style.background-color]="currentPostsPage() === item ? 'var(--color-primary)' : null"
              [style.border-color]="currentPostsPage() === item ? 'var(--color-primary)' : null"
              [style.color]="currentPostsPage() === item ? '#ffffff' : null"
              type="button"
              [attr.aria-current]="currentPostsPage() === item ? 'page' : null"
              (click)="goToPostsPage(item)"
            >
              {{ item }}
            </button>
          }
        }
        <button
          class="w-14 h-14 rounded-2xl flex items-center justify-center text-slate-900 border border-slate-100 hover:text-[var(--color-primary)] transition shadow-sm bg-white disabled:cursor-not-allowed disabled:opacity-40"
          type="button"
          [disabled]="currentPostsPage() === postsTotalPages()"
          (click)="goToNextPostsPage()"
        >
          <svg viewBox="0 0 24 24" fill="none" class="w-6 h-6" aria-hidden="true">
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </button>
      </div>
    }
  </div>
</section>

<app-big-sale-banner />

<section class="sav-faq-section" aria-label="Frequently Asked Questions">
  <div class="sav-faq-wrap">
    <h2>Frequently Asked Questions</h2>
    <p>Common questions about our guides and how we verify offers.</p>
    <div class="sav-faq-list">
      @for (faq of displayedFaqs(); track $index) {
        <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === $index">
          <button
            class="sav-faq-question"
            type="button"
            [attr.aria-expanded]="expandedFaqIndex() === $index"
            (click)="toggleFaq($index)"
          >
            <span>{{ faq.question }}</span>
            <svg
              class="sav-faq-chevron"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === $index">
            <div class="sav-faq-answer-body" [innerHTML]="faq.answer | trustedCmsHtml"></div>
          </div>
        </div>
      }
    </div>
  </div>
</section>
